import React, { useState } from 'react';
import { Controller, FieldError, useFormContext } from 'react-hook-form';
import get from 'lodash/get';
import { Select } from '../../../../CommonComponents/OPL/Select/Select/Select';
import { employmentStatusOpts } from './EmploymentStatus.meta';
import { SpaceProps } from '../../../../theme/styledProps';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { Input } from '../../../../CommonComponents/OPL/Input/Input';
import {
  DraftAccountDTO,
  EmploymentStatus as EmploymentStatusEnum,
} from '../../AccountOpening.meta';
import { EditableField } from '../../components/EditableField/EditableField';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';

interface Props extends SpaceProps {
  isReview?: boolean;
  fieldName: string;
  draftAccount: DraftAccountDTO;
}

export const EmploymentStatus: React.FC<Props> = ({
  isReview,
  fieldName,
  draftAccount,
  ...rest
}) => {
  const formBag = useFormContext();
  const {
    formState: { errors },
  } = formBag;

  const [isEditing, setIsEditing] = useState(!isReview);

  const statusFieldName = `${fieldName}.employmentStatus`;
  const companyFieldName = `${fieldName}.employerName`;
  const positionFieldName = `${fieldName}.positionEmployed`;

  const employmentStatus = formBag.watch(statusFieldName);

  if (!isEditing) {
    return (
      <EditableField onEdit={() => setIsEditing(true)}>
        <Text fontWeight={500}>
          {
            employmentStatusOpts.find(({ value }) => value === get(draftAccount, statusFieldName))
              ?.label
          }
        </Text>
      </EditableField>
    );
  }

  return (
    <Box {...rest}>
      <Controller
        control={formBag.control}
        name={statusFieldName}
        render={({ field }) => (
          <Select
            value={employmentStatusOpts.find(({ value }) => value === field.value)}
            label={isReview ? undefined : 'Employment Status'}
            onChange={opt => {
              if (opt) {
                formBag.setValue(statusFieldName, opt.value);
              }
            }}
            options={employmentStatusOpts}
            formVariant={get(errors, statusFieldName) ? 'error' : undefined}
            helperText={(get(errors, statusFieldName) as FieldError | undefined)?.message}
            aria-label="Employment Status"
          />
        )}
      />

      {employmentStatus === EmploymentStatusEnum.Employed && (
        <>
          <Box mt={isReview ? '8' : '32'}>
            <Input
              {...formBag.register(companyFieldName)}
              label={isReview ? undefined : 'Company Name'}
              type="text"
              placeholder={isReview ? 'Company Name' : 'E.g. Microsoft'}
              formVariant={get(errors, companyFieldName) ? 'error' : undefined}
              helperText={(get(errors, companyFieldName) as FieldError | undefined)?.message}
              aria-label="Company Name"
              maxLength={256}
            />
          </Box>

          <Box mt={isReview ? '8' : '32'}>
            <Input
              {...formBag.register(positionFieldName)}
              label={isReview ? undefined : 'Job Title'}
              type="text"
              placeholder={isReview ? 'Job Title' : 'What is your position?'}
              formVariant={get(errors, positionFieldName) ? 'error' : undefined}
              helperText={(get(errors, positionFieldName) as FieldError | undefined)?.message}
              aria-label="Job Title"
              maxLength={256}
            />
          </Box>
        </>
      )}
    </Box>
  );
};
